import merge from "lodash.merge";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { DataSelectors, UiStateSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineSelectors,
	FormEngineViews
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import {
	DefaultFormModelMap,
	DefaultRepeatButtonNames,
	DefaultWidgetMap,
	EnablementEntry,
	FormModelMap,
	WidgetMap
} from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import {
	EnablementByButtonName,
	EnablementByRow
} from "@com.mgmtp.a12.formengine/formengine-core/lib/view/internal/configuration/engine-configuration";
import { DocumentServiceFactory, EntityInstancePath, GroupInstance } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import {
	UaaExtendedUser,
	UaaOidcModifiedUser,
	UaaSelectors,
	UaaUser
} from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { UaaOidcUser } from "@com.mgmtp.a12.uaa/uaa-authentication-client/lib/internal/interfaces";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import {
	ButtonGroupProps,
	DefaultFileUploadProps,
	ModalNotificationProps,
	RadioProps,
	Select,
	TextAreaStateless,
	TimePicker
} from "@com.mgmtp.a12.widgets/widgets-core";
import { ButtonGroup } from "@com.mgmtp.a12.widgets/widgets-core/lib/button-group/main/button-group.view";
import { Button } from "@com.mgmtp.a12.widgets/widgets-core/lib/button/main/button.view";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon/main/icon.view";
import { Checkbox } from "@com.mgmtp.a12.widgets/widgets-core/lib/input/checkbox";
import { ModalNotification } from "@com.mgmtp.a12.widgets/widgets-core/lib/modal-notification/main/modal-notification.view";
import { ButtonGroupContainerProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/button-group-container";

import { hasAnnotation } from "../../../antragstellungs-client/utils/formModelHelper";
import { getFVManifest, getLatestFVManifestByKey } from "../../../app/slices/fachverfahren/selectors";
import { MappingElement, RuntimeFachverfahrenManifest } from "../../../app/slices/fachverfahren/types";
import { AssigneeCandidate } from "../../../app/slices/users/types";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { assigneePickerDecorator } from "../../../component/assigneePickerDecorator";
import { inputAriaRequiredDecorator } from "../../../component/inputAriaRequiredDecorator";
import { RESOURCE_KEYS } from "../../../localization";
import { assertNonNullable, assertTrue } from "../../../utilities/assert";
import { isGroupInstanceArray } from "../../../utilities/documentHelpers";
import { getValuesFromModelPath, isSubPath } from "../../../utilities/pathHelpers";
import { hasUpdateDocumentPermission } from "../../../utilities/permissionUtils";
import { isDocument, isString } from "../../../utilities/typeGuardUtils";
import { isAllowedRuntimeModule, selectHasNeededPermissions, selectIsAdmin } from "../../../utilities/user";
import { PATH_TO_ASSIGNEE_GROUP } from "../../../utilities/variables/documentModelPaths";
import {
	BUTTON_DEPENDENT_ENABLEMENT,
	FILEUPLOAD_FORM_MODEL,
	FILEUPLOAD_INITIAL_ANGEHAENGT_FORM_MODEL,
	RUNTIME_MODULE_CHECK_ROLES,
	VORGANGSNUMMER
} from "../../../utilities/variables/modulfRuntimeAnnotations";
import { FormModelComponentCollector } from "../../../workaround/internal/form-model-utils";
import { ModelWalker } from "../../../workaround/internal/form-model-walker";
import { assignProcessDocument } from "../../assignment/actions";
import { DocumentAssigneePicker } from "../../assignment/components/AssigneePicker";
import { selectAssigneeCandidatesCreator } from "../../assignment/selectors";
import { getViewHtmlId } from "../../focusHandling/sagas";
import { ANTRAG_DETAIL_ACTIVITY_ID } from "../../processOverview/store/actions";
import { isDocumentWithProcessMetadata } from "../../workflows/data/processMetadata";
import { getLockProcessError, isProcessLocked } from "../../../utilities/lockProcessUtils";
import { selectOperationLockState } from "../../../app/slices/operation/selectors";
import { EpayblAmountInput } from "../../zahlungsvorgangEpaybl/components/EpayblAmountInput";

import { useExternalEnumerationProvider } from "../hooks/useExternalEnumeration";
import { isCustomProcessActivityData } from "../types";

import { ContentBoxWithWizard } from "./contentBoxWithWizard";
import { ActivityContext, ControlContext } from "./contexts";
import { CustomAutocomplete } from "./customAutocomplete";
import { CustomButton } from "./customButton";
import { CustomControl } from "./CustomControl";
import { CustomEventButton } from "./customEventButton";
import { CustomFieldOverviewColumn } from "./CustomFieldOverviewColumn";
import { CustomHintTooltip } from "./customHintTooltip";
import { CustomInlineRepeat } from "./CustomInlineRepeat";
import { CustomMultiselect } from "./customMultiselect";
import { CustomTextCell } from "./CustomTextCell";
import { EqualizeRowLabelHeights } from "./EqualizeRowLabelHeights";

const documentService = new DocumentServiceFactory().getDocumentService();

const widgetMap: WidgetMap = {
	...DefaultWidgetMap,
	ActionContentbox: ContentBoxWithWizard,
	Autocomplete: inputAriaRequiredDecorator(assigneePickerDecorator(CustomAutocomplete)),
	TextLineStateless: inputAriaRequiredDecorator(EpayblAmountInput),
	TextAreaStateless: inputAriaRequiredDecorator(TextAreaStateless),
	Checkbox: inputAriaRequiredDecorator(Checkbox),
	MultiSelect: inputAriaRequiredDecorator(CustomMultiselect),
	TimePicker: inputAriaRequiredDecorator(TimePicker),
	Select: inputAriaRequiredDecorator(Select),
	ButtonGroup: (props: ButtonGroupProps) => {
		// File upload: don't allow replacing files if the file has the "initialAngehaengt" flag (= is immutable)
		const swappedChildren = React.Children.map(props.children, it =>
			React.isValidElement(it) && it.key === "duplicates-replace" ? null : it
		);
		return <DefaultWidgetMap.ButtonGroup {...props}>{swappedChildren}</DefaultWidgetMap.ButtonGroup>;
	},
	Button: CustomButton,
	ButtonGroupContainer: (props: ButtonGroupContainerProps) => {
		return <DefaultWidgetMap.ButtonGroupContainer {...props} collapsingDirection={"left-to-right"} />;
	},
	HintTooltip: CustomHintTooltip,
	Radio: (props: RadioProps) => (
		<DefaultWidgetMap.Radio
			{...props}
			groupDOMProps={{
				...props.groupDOMProps,
				"aria-required": React.useContext(ControlContext)?.isRequired
			}}
		/>
	),
	ModalNotification: (props: ModalNotificationProps) => {
		const { onClose: defaultOnClose } = props;

		const activityId = React.useContext(ActivityIdContext);
		assertNonNullable(activityId);

		const onClose = React.useCallback(() => {
			if (defaultOnClose) {
				defaultOnClose();
			}

			const id = getViewHtmlId(activityId);
			const element = id ? document.getElementById(id) : null;

			// TODO: Wenn in einem File Upload eine Datei hochgeladen wurde und diese gelöscht werden soll, wird beim Abbrechen
			//  im Modal der Fokus auf das gesamte Fenster gesetzt. Sollte auf Contentbereich der Activity liegen
			element?.focus();
		}, [defaultOnClose, activityId]);

		// TODO: Wenn Element nach dem Schließen auf dem Screen existiert, soll Fokus darauf springen. Ansonsten auf den Contentbereich der Activity
		return <DefaultWidgetMap.ModalNotification {...props} onClose={onClose} />;
	},
	DefaultFileUpload: (props: DefaultFileUploadProps) => {
		if (!props.compact) {
			return <DefaultWidgetMap.DefaultFileUpload {...props} />;
		}

		const extendedCallback = () => {
			if (props.onUploadAreaClick) {
				// Normalerweise wird der User hier dazu aufgefordert, das Verlassen der Seite zu bestätigen. Da die
				// Seite allerdings nicht verlassen wird, sondern sich lediglich das Dokument in einem neuen Tab öffnet,
				// wird die Anzeige des entsprechenden Prompts unterdrückt.
				window.suppressClosePrompt = true;
				props.onUploadAreaClick();
			}
		};

		return (
			<DefaultWidgetMap.DefaultFileUpload {...props} uploadAreaSize={{}} onUploadAreaClick={extendedCallback} />
		);
	}
};

/**
 * Custom Form-Engine mit einem Process Step Wizard.
 * Die folgenden Konventionen müssen in den Modellen gelten, damit dieser funktioniert:
 *
 * 1. Zu jedem Prozessschritt muss ein Form-Model existieren.
 *
 * 2. Jedes Form-Model enthält einen Screen für die Daten, die für den Schritt wichtig sind
 * plus einen Screen für jeden vorherigen Prozessschritt (damit man sich die vorherigen
 * Daten angucken kann).
 *
 * 3. Die ID des Prozessschrittes muss den gleichen Namen haben, wie der zugehörige Screen.
 *
 * 4. In jedem Form-Model existiert in den Model-Settings eine Annotation mit einer Auflistung
 * aller Prozessschritte (tasks)
 *
 * Anmerkungen: Konvention 2, 3, 4 und 5 könnten über einen Generator gelöst werden.
 * Dieser bräuchte einmal alle Form-Modelle und dann eine Definition der Prozessschritte.
 *
 * Beispiel:
 * Prozess:
 *     Schritt 1: Antrag Prüfen - Prozessschritt ID "AntragPruefen"
 *   Schritt 2: Antrag Genehmigen - Prozessschritt ID "AntragGenehmigen"
 *
 *
 * Form-Model 1: Form-Model für Antrag Prüfen:
 *     * Hat zu erst einen Screen namens "AntragPruefen".
 *   * Hat außerdem einen Screen namens "AntragErfassen" der gleich dem Screen "AntragErfassen" aus Form-Model 1 ist.
 *   * Hat eine Annotation in den Settings mit der Liste der Tasks.
 *
 * Form-Model 2: Form-Model für Antrag Genehmigen:
 *     * Hat zu erst einen Screen namens "AntragGenehmigen".
 *   * Hat außerdem einen Screen namens "AntragErfassen" der gleich dem Screen "AntragErfassen" aus Form-Model 1 ist.
 *   * Hat außerdem einen Screen namens "AntragPruefen" der gleich dem Screen "AntragPruefen" aus Form-Model 2 ist.
 *   * Hat eine Annotation in den Settings mit der Liste der Tasks.
 *
 * Dokumentation getA12:
 *    Client Keywords:
 *        Form-Engine (Features > Engines > Form-Engine)
 *        Mobile Navigation (Features > Mobile Navigation)
 *
 *  Form-Engine Keywords:
 *      WidgetMap (Form-Engine > Customization > Rendering)
 */
export function ProcessFormEngine(props: FormEngineViews.FormEngineProps): React.ReactNode {
	const activityId = props.activityId;
	const fvId = useSelector(ActivitySelectors.activityPropById(activityId, a => a.descriptor.fachverfahren));
	const docRef = useSelector(ActivitySelectors.activityPropById(activityId, a => a.descriptor.instance));
	assertNonNullable(docRef);
	const engineState = useSelector(FormEngineSelectors.engineState(activityId));
	const configuration = props.configuration as Record<string, unknown>;
	const fvManifest = useSelector(getFVManifest(fvId ?? ""));
	const latestFvManifest = useSelector(getLatestFVManifestByKey(fvManifest?.identification.key ?? ""));
	const data = useSelector(ActivitySelectors.data(activityId));
	const isReadonly = engineState && UiStateSelectors.readonly()(engineState);
	const canEdit =
		useSelector(ActivitySelectors.activityPropById(activityId, a => a.descriptor.editable === "true")) ?? false;
	const formModel = engineState?.models.formModel;
	const document = engineState && DataSelectors.document()(engineState);
	const currenScreenLocation = engineState && UiStateSelectors.currentScreenLocation()(engineState);
	const isAdminUser = useSelector(selectIsAdmin);
	const hasEditPermission = useSelector(
		selectHasNeededPermissions(hasUpdateDocumentPermission, fvManifest?.identification.key)
	);

	const user = useSelector(UaaSelectors.user);
	assertNonNullable(user);

	const lockState = useSelector(selectOperationLockState(docRef));

	const isFinished = isDocumentWithProcessMetadata(document) && document.root.processMetadata.isFinished;
	const fqn = isDocumentWithProcessMetadata(document) ? document.root.processMetadata.fachverfahren.fqn : undefined;

	const sourceProcessId =
		FormActivity.Data.SingleDocumentData.isInstance(data) && isDocumentWithProcessMetadata(data.document)
			? data.document.root.processMetadata.links?.sourceProcessId
			: undefined;

	const byRowEnablements: EnablementByRow | undefined = React.useMemo(() => {
		return formModel && currenScreenLocation?.path && isDocument(document)
			? getRowEnablements(formModel, document, currenScreenLocation.path, isAdminUser)
			: undefined;
	}, [formModel, document, currenScreenLocation, isAdminUser]);

	const byButtonNameEnablements = React.useMemo(() => {
		return getButtonEnablements(
			canEdit,
			isReadonly,
			latestFvManifest,
			formModel,
			user,
			isFinished,
			hasEditPermission,
			sourceProcessId
		);
	}, [isReadonly, canEdit, latestFvManifest, formModel, user, isFinished, hasEditPermission, sourceProcessId]);

	const enablements = { byButtonName: byButtonNameEnablements, byRow: byRowEnablements };

	const hasNavigation =
		ANTRAG_DETAIL_ACTIVITY_ID !== activityId &&
		!((configuration && hasHiddenNavigationConfiguration(configuration)) || false);

	const pathToVorgangsnummer = fvManifest?.mappingElement.find(
		(x: MappingElement) => x.alias === VORGANGSNUMMER
	)?.path;

	const vorgangsnummer =
		document &&
		documentService.getAssignedObject(document as GroupInstance, pathToVorgangsnummer as EntityInstancePath);

	const archivalInProgress = isDocumentWithProcessMetadata(document)
		? document.root.processMetadata.archival?.archivalInProgress
		: undefined;

	const processLocked = isProcessLocked(lockState, user);
	const lockProcessErrorMessage: string = getLockProcessError(lockState);
	const externalEnumerationProvider = useExternalEnumerationProvider(activityId);

	const formModelMap: FormModelMap = React.useMemo(
		() => ({
			...DefaultFormModelMap,
			TextCell: { component: CustomTextCell },
			EventButton: { component: CustomEventButton(latestFvManifest) },
			InlineRepeat: {
				component: CustomInlineRepeat(activityId, isReadonly)
			},
			Control: {
				component: CustomControl(activityId)
			},
			FieldOverviewColumn: { component: CustomFieldOverviewColumn },
			TableBodyCell: { component: () => <>ExpressionCell</> },
			Row: { component: EqualizeRowLabelHeights }
		}),
		[activityId, isReadonly, latestFvManifest]
	);

	const activityContextValue = React.useMemo(
		() => ({
			activityId,
			isReadonly,
			hasNavigation,
			documentService,
			fvManifest,
			vorgangsnummer: isString(vorgangsnummer) ? vorgangsnummer : undefined,
			archivalInProgress: archivalInProgress,
			isProcessLocked: processLocked,
			lockProcessErrorMessage
		}),
		[
			activityId,
			isReadonly,
			hasNavigation,
			fvManifest,
			vorgangsnummer,
			archivalInProgress,
			processLocked,
			lockProcessErrorMessage
		]
	);

	const dispatch = useDispatch();
	assertTrue(isCustomProcessActivityData(data));
	const showAssignmentModal = data?.showAssignmentModal;

	const { localizer } = React.useContext(LocalizerContext);

	const fvKey = fvManifest?.identification.key ?? "";
	const candidates = useSelector(selectAssigneeCandidatesCreator(fvKey, "Vorgangszuweisung"));
	const onChange = React.useCallback<(assignee: AssigneeCandidate | undefined) => void>(
		assignee => dispatch(assignProcessDocument({ docRef, activityId, assignee: assignee?.username ?? "" })),
		[activityId, dispatch, docRef]
	);

	const handleCloseAssignmentModal = React.useCallback(
		function handleCloseAssignmentModal(): void {
			dispatch(ActivityActions.setData({ activityId, data: { ...data, showAssignmentModal: false } }));
		},
		[activityId, data, dispatch]
	);
	if (!engineState) {
		return null;
	}

	return (
		<>
			{showAssignmentModal && isDocumentWithProcessMetadata(document) && fvKey && (
				<>
					<ModalNotification
						icon={<Icon>account_circle</Icon>}
						title="Zuweisung ändern"
						enableCloseButton
						closeOnEsc
						closeOnOutsideClick
						focusOnOpen
						onClose={handleCloseAssignmentModal}
						id="assignment-modal"
						footer={
							<ButtonGroup alignment="right">
								<Button
									label={localizer({ key: RESOURCE_KEYS.features.modals.button.close })}
									secondary
									onClick={handleCloseAssignmentModal}
								/>
							</ButtonGroup>
						}
					>
						<DocumentAssigneePicker
							id={`view-${props.activityId}-assignee-picker`}
							label="Zugewiesen an"
							fvKey={fvKey}
							fqn={fqn}
							document={document}
							instancePath={INSTANCE_PATH_TO_ASSIGNEE_GROUP}
							candidates={candidates}
							onChange={onChange}
						/>
					</ModalNotification>
				</>
			)}

			<ActivityIdContext.Provider value={activityId}>
				<ActivityContext.Provider value={activityContextValue}>
					<FormEngineViews.FormEngine
						{...props}
						widgetMap={widgetMap}
						formModelMap={formModelMap}
						enablements={enablements}
						externalEnumerationProvider={externalEnumerationProvider}
					/>
				</ActivityContext.Provider>
			</ActivityIdContext.Provider>
		</>
	);
}

function getButtonEnablements(
	canEdit: boolean,
	readonly?: boolean,
	fvManifest?: RuntimeFachverfahrenManifest,
	formModel?: FormModel,
	user?: UaaUser | UaaOidcUser | UaaExtendedUser | UaaOidcModifiedUser,
	isFinished?: boolean,
	hasEditPermission?: boolean,
	sourceProcessId?: string
): EnablementByButtonName {
	assertNonNullable(user);

	const subheaderButtons = [
		...(formModel?.content?.subHeaderBox?.minorButtons?.button ?? []),
		...(formModel?.content?.subHeaderBox?.majorButtons?.button ?? [])
	];

	const unauthorisedSubheaderButtons = subheaderButtons.filter(it =>
		it.annotations?.find(
			e => e.name === RUNTIME_MODULE_CHECK_ROLES && !isAllowedRuntimeModule(user, e.value!, fvManifest)
		)
	);
	const buttonEnablements = {
		ShowFristenButton: { hidden: !fvManifest?.runtimeModules?.some(it => it.name === "Fristen") },
		ShowChangelogButton: { hidden: !fvManifest?.runtimeModules?.some(it => it.name === "Changelog") },
		ShowLinksButton: {
			hidden: !fvManifest?.runtimeModules?.some(it => it.name === "FachverfahrenMapping") && !sourceProcessId
		},
		ChangeAssigneeeButton: { hidden: isFinished, disabled: !hasEditPermission },
		...unauthorisedSubheaderButtons.reduce<Record<string, EnablementEntry>>((enablements, current) => {
			enablements[current.name] = { hidden: true };
			return enablements;
		}, {})
	};

	if (canEdit) {
		return {
			...buttonEnablements,
			edit_speichern: {
				hidden: readonly
			},
			edit_abbrechen: {
				hidden: readonly
			},
			Abschließen: {
				hidden: true
			},
			devFillFields: {
				hidden: true
			},
			Speichern: {
				hidden: true
			},
			Abbrechen: {
				hidden: true
			}
		};
	}

	return {
		...buttonEnablements,
		edit_speichern: {
			hidden: true
		},
		edit_abbrechen: {
			hidden: true
		}
	};
}

const INITIAL_ANGEHAENGT_FIELD_SUBPATH = "initialAngehaengt";

function getRowEnablements(
	formModel: FormModel,
	document: Activity.Data.Document,
	currentScreenPath: EntityInstancePath,
	isAdminUser: boolean
): EnablementByRow | undefined {
	const formModelComponentCollector = new FormModelComponentCollector();
	new ModelWalker(formModelComponentCollector).acceptModel(formModel);
	const repeats = formModelComponentCollector.getRepeats();

	const enablements: EnablementByRow[] = repeats
		.map(repeat => collectEnablementFromRepeat(repeat, document, isAdminUser))
		.filter(repeat => repeat !== undefined)
		.map(repeat => repeat as EnablementByRow);

	repeats
		.filter(
			repeat => repeat.rowActionGroup && repeat.annotations?.some(a => a.name === BUTTON_DEPENDENT_ENABLEMENT)
		)
		.forEach(repeat =>
			repeat.rowActionGroup?.action
				?.filter(action => action.annotations?.some(a => a.name === BUTTON_DEPENDENT_ENABLEMENT))
				.forEach(action =>
					enablements.push(...createRowEnablementEntry(repeat, action, document, currentScreenPath))
				)
		);

	if (enablements.length > 0) {
		return enablements.reduce((a, b) => merge(a, b), {});
	}

	return undefined;
}

function collectEnablementFromRepeat(
	repeat: FormModel.Repeat,
	document: Activity.Data.Document,
	isAdminUser: boolean
): EnablementByRow | undefined {
	if (
		!hasAnnotation(repeat, FILEUPLOAD_FORM_MODEL) &&
		!hasAnnotation(repeat, FILEUPLOAD_INITIAL_ANGEHAENGT_FORM_MODEL)
	) {
		return undefined;
	}

	const groupInstances = documentService.getAssignedObject(
		document,
		repeat.groupPath.map((it, index) => ({
			elementName: it.elementName,
			index: index === repeat.groupPath.length - 1 ? 0 : 1
		}))
	);

	if (!groupInstances) {
		return undefined;
	}

	assertTrue(isGroupInstanceArray(groupInstances));
	const hiddenIndices = isAdminUser
		? []
		: groupInstances
				.map((it, index) => (it[INITIAL_ANGEHAENGT_FIELD_SUBPATH] === true ? index : null))
				.filter(it => it !== null)
				.map(it => it as number);

	const indexedDeleteEnablements = hiddenIndices.reduce(
		(deleteEnablements, index) => {
			deleteEnablements[(index + 1) as unknown as string] = { disabled: true }; // A12 indices are 1-based
			return deleteEnablements;
		},
		{} as Record<string, EnablementEntry>
	);

	return {
		[repeat.name]: {
			[DefaultRepeatButtonNames.delete]: indexedDeleteEnablements
		}
	};
}

function createRowEnablementEntry(
	repeat: FormModel.Repeat,
	action: FormModel.RowAction,
	document: Activity.Data.Document,
	currentScreenPath: EntityInstancePath
): EnablementByRow[] {
	if (!repeat.annotations || !action.annotations || !isDocument(document)) {
		return [];
	}

	const repeatGroupPath = repeat.annotations.find(a => a.name === BUTTON_DEPENDENT_ENABLEMENT)?.value;
	if (!repeatGroupPath) {
		return [];
	}
	const repeatGroupIndex = repeat.groupPath.length - 1;

	const pathToField = action.annotations.find(a => a.name === BUTTON_DEPENDENT_ENABLEMENT)?.value;
	if (!pathToField) {
		return [];
	}

	return getValuesFromModelPath(ModelPath.fromString(pathToField).slice(1), document)
		.filter(e => isSubPath(currentScreenPath, e.path))
		.map(e => ({
			[repeat.name]: {
				[action.event]: {
					[e.path[repeatGroupIndex].index.toString()]: {
						hidden: e.value !== undefined
					}
				}
			}
		}));
}

function hasHiddenNavigationConfiguration(el: Record<string, unknown>): el is { hiddenNavigation: string } {
	return typeof el.hiddenNavigation === "string";
}

const INSTANCE_PATH_TO_ASSIGNEE_GROUP = PATH_TO_ASSIGNEE_GROUP.map(it => ({ ...it, index: 1 }));
